import { Link, useParams } from "react-router-dom"
import { FaWhatsapp, FaArrowLeft } from "react-icons/fa"

import { products } from "../data/products"
import SEO from "../components/common/SEO"

export default function ProductDetails() {
  const { slug } = useParams()

  const product = products.find(
    (item) => item.slug === slug
  )

  if (!product) {
    return (
      <section className="py-20">
        <div className="container mx-auto px-4 text-center">
          <h1 className="text-4xl font-bold text-gray-900">
            Produit introuvable
          </h1>

          <p className="mt-4 text-gray-600">
            Le produit demandé n'existe pas ou n'est plus disponible.
          </p>

          <Link
            to="/produits"
            className="inline-flex items-center gap-2 mt-8 bg-gray-900 text-white px-6 py-3 rounded-xl hover:bg-black transition"
          >
            <FaArrowLeft />
            Retour aux produits
          </Link>
        </div>
      </section>
    )
  }

  const relatedProducts = products
  .filter(
    (item) =>
      item.category === product.category &&
      item.slug !== product.slug
  )
  .sort((a, b) => {
    if (a.featured && !b.featured) return -1
    if (!a.featured && b.featured) return 1
    return 0
  })
  .slice(0, 4)

  const whatsappMessage = encodeURIComponent(
    `Bonjour, je souhaite obtenir des informations sur : ${product.name}`
  )

  return (
    <>
      <SEO
        title={product.name}
        description={
          product.description ||
          `Découvrez ${product.name}.`
        }
        keywords={`${product.name}, ${product.category}, textile, mercerie`}
        url={`https://www.khenissitextiles.com/produits/${slug}`}
      />

      <section className="py-12 lg:py-20">
        <div className="container mx-auto px-4 lg:px-8">

          {/* Breadcrumb */}
          <div className="mb-8 text-sm text-gray-500">
            <Link
              to="/"
              className="hover:text-gray-900"
            >
              Accueil
            </Link>

            {" / "}

            <Link
              to="/produits"
              className="hover:text-gray-900"
            >
              Produits
            </Link>

            {" / "}

            <span className="text-gray-900">
              {product.name}
            </span>
            {product.featured && (
  <span className="inline-flex mt-4 px-3 py-1 rounded-full bg-blue-100 text-blue-700 text-sm font-medium">
    Produit recommandé
  </span>
)}
          </div>

          {/* Produit */}
          <div className="grid lg:grid-cols-2 gap-12 items-start">

            {/* Image */}
            <div>
              <div className="overflow-hidden rounded-3xl border border-gray-200 bg-white">
                <img
                  src={product.image}
                  alt={product.name}
                  className="w-full object-cover"
                />
              </div>
            </div>

            {/* Infos */}
            <div>

              <span className="inline-flex px-4 py-2 rounded-full bg-gray-100 text-gray-700 text-sm font-medium">
                {product.category}
              </span>

              <h1 className="mt-4 text-4xl lg:text-5xl font-bold text-gray-900">
                {product.name}
              </h1>

              {product.description && (
                <p className="mt-6 text-lg text-gray-600 leading-relaxed">
                  {product.description}
                </p>
              )}

              {/* Couleurs */}
              {product.colors?.length > 0 && (
                <div className="mt-8">

                  <h3 className="font-semibold text-gray-900 mb-4">
                    Couleurs disponibles
                  </h3>

                  <div className="flex flex-wrap gap-3">

                    {product.colors.map((color) => (
                      <div
                        key={color.name}
                        className="flex items-center gap-2"
                      >
                        <span
                          className="w-7 h-7 rounded-full border border-gray-300"
                          style={{
                            backgroundColor: color.code,
                          }}
                        />

                        <span className="text-sm text-gray-700">
                          {color.name}
                        </span>
                      </div>
                    ))}

                  </div>
                </div>
              )}

              {/* Dimensions */}
              {product.sizes?.length > 0 && (
                <div className="mt-8">

                  <h3 className="font-semibold text-gray-900 mb-4">
                    Dimensions disponibles
                  </h3>

                  <div className="flex flex-wrap gap-2">

                    {product.sizes.map((size) => (
                      <span
                        key={size}
                        className="px-4 py-2 bg-gray-100 rounded-lg text-sm"
                      >
                        {size}
                      </span>
                    ))}

                  </div>
                </div>
              )}

              {/* CTA */}
              <div className="mt-10 flex flex-col sm:flex-row gap-4">

                <a
                  href={`https://wa.me/21628718432?text=${whatsappMessage}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center gap-2 bg-green-600 hover:bg-green-700 text-white px-6 py-4 rounded-xl font-semibold transition"
                >
                  <FaWhatsapp />
                  Demander un devis
                </a>

                <Link
                  to="/produits"
                  className="inline-flex items-center justify-center gap-2 border border-gray-300 hover:bg-gray-100 px-6 py-4 rounded-xl font-semibold transition"
                >
                  <FaArrowLeft />
                  Retour aux produits
                </Link>

              </div>

            </div>

          </div>

          {/* Produits similaires */}
          {relatedProducts.length > 0 && (
            <div className="mt-24">

              <h2 className="text-3xl font-bold text-gray-900 mb-8">
                Produits similaires
              </h2>

              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">

                {relatedProducts.map((item) => (
                  <Link
                    key={item.slug}
                    to={`/produits/${item.slug}`}
                    className="group bg-white border border-gray-200 rounded-2xl overflow-hidden hover:shadow-lg transition"
                  >
                    <img
                      src={item.image}
                      alt={item.name}
                      className="w-full aspect-square object-cover group-hover:scale-105 transition duration-500"
                    />

                    <div className="p-4">
                      <h3 className="font-semibold text-gray-900">
                        {item.name}
                      </h3>
                    </div>
                  </Link>
                ))}

              </div>

            </div>
          )}

        </div>
      </section>
    </>
  )
}